import React from 'react';
import { CornerUpRight, Calculator, GitBranch, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';

/**
 * Компонент для учета местных сопротивлений в трубопроводе
 */
const LocalResistanceSection = ({ formData, handleInputChange, errors, connectionTypes }) => {
  if (!formData.includeLocalResistance) {
    return (
      <div className="mb-6">
        <div className="flex items-center">
          <label className="inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              name="includeLocalResistance"
              checked={formData.includeLocalResistance}
              onChange={handleInputChange}
              className="form-checkbox"
            />
            <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Учитывать местные сопротивления (повороты, вентили, тройники)
            </span>
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="flex items-center mb-4">
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            name="includeLocalResistance"
            checked={formData.includeLocalResistance}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            Учитывать местные сопротивления
          </span>
        </label>
      </div>

      <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-100 dark:border-indigo-800">
        <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-3">
          Местные сопротивления
        </h4>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Количество поворотов */}
          <div>
            <label htmlFor="bendCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <CornerUpRight className="w-4 h-4 mr-2 text-indigo-500" />
              Количество поворотов 90°
            </label>
            <input
              type="text"
              id="bendCount"
              name="bendCount"
              value={formData.bendCount}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.bendCount ? 'input-error' : ''}`}
              placeholder="Введите количество"
            />
            {errors.bendCount && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.bendCount}
              </p>
            )}
          </div>

          {/* Количество вентилей */}
          <div>
            <label htmlFor="CalculatorCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Calculator className="w-4 h-4 mr-2 text-indigo-500" />
              Количество вентилей
            </label>
            <input
              type="text"
              id="CalculatorCount"
              name="CalculatorCount"
              value={formData.CalculatorCount}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.CalculatorCount ? 'input-error' : ''}`}
              placeholder="Введите количество"
            />
            {errors.CalculatorCount && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.CalculatorCount}
              </p>
            )}
          </div>

          {/* Количество тройников */}
          <div>
            <label htmlFor="teeCount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <GitBranch className="w-4 h-4 mr-2 text-indigo-500" />
              Количество тройников
            </label>
            <input
              type="text"
              id="teeCount"
              name="teeCount"
              value={formData.teeCount}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.teeCount ? 'input-error' : ''}`}
              placeholder="Введите количество"
            />
            {errors.teeCount && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.teeCount}
              </p>
            )}
          </div>

          {/* Тип входа в трубу */}
          <div>
            <label htmlFor="entranceType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <ArrowDownCircle className="w-4 h-4 mr-2 text-indigo-500" />
              Тип входа в трубу
            </label>
            <select
              id="entranceType"
              name="entranceType"
              value={formData.entranceType}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              {connectionTypes.map((type) => (
                <option key={`entrance-${type.id}`} value={type.id}>
                  {type.name} (ξ = {type.factor})
                </option>
              ))}
            </select>
          </div>

          {/* Тип выхода из трубы */}
          <div>
            <label htmlFor="exitType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <ArrowUpCircle className="w-4 h-4 mr-2 text-indigo-500" />
              Тип выхода из трубы
            </label>
            <select
              id="exitType"
              name="exitType"
              value={formData.exitType}
              onChange={handleInputChange}
              className="form-select w-full"
            >
              {connectionTypes.map((type) => (
                <option key={`exit-${type.id}`} value={type.id}>
                  {type.name} (ξ = {type.factor})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Информация о местных сопротивлениях */}
        <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-indigo-100 dark:border-indigo-800 text-xs text-gray-600 dark:text-gray-400">
          <h5 className="font-medium text-gray-900 dark:text-white mb-1">Справочная информация:</h5>
          <ul className="list-disc pl-4 space-y-1">
            <li><strong>Поворот на 90°</strong> - коэффициент сопротивления ξ = 0.3</li>
            <li><strong>Вентиль (полностью открытый)</strong> - коэффициент сопротивления ξ = 3.0</li>
            <li><strong>Тройник (проход)</strong> - коэффициент сопротивления ξ = 1.5</li>
            <li><strong>Тройник (ответвление)</strong> - коэффициент сопротивления ξ = 2.0</li>
            <li><strong>Внезапное расширение</strong> - коэффициент сопротивления ξ = 1.0</li>
            <li><strong>Внезапное сужение</strong> - коэффициент сопротивления ξ = 0.5</li>
          </ul>
          <p className="mt-2">
            Большее значение коэффициента сопротивления ξ означает большие потери давления. 
            Учет местных сопротивлений особенно важен в коротких трубопроводах или системах с большим 
            количеством фитингов.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LocalResistanceSection;